import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Award, Sparkles, ChevronRight, Check, Star, TrendingUp, Users } from "lucide-react";

// Plan feature highlights to display in the modal
const PLAN_HIGHLIGHTS = [
  {
    icon: <TrendingUp className="text-purple-500" />,
    title: "More leads, better results",
    description: "Access premium sources and more accurate lead detection",
  },
  {
    icon: <Users className="text-indigo-500" />,
    title: "Reach more potential customers",
    description: "Target a wider audience with expanded keyword limits",
  },
  {
    icon: <Sparkles className="text-amber-500" />,
    title: "AI-powered insights",
    description: "Get higher quality response suggestions tailored to each lead",
  },
];

const UpgradeModal = ({ isOpen, onClose, upgradeMessage, handleUpgradeClick }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 400,
          duration: 0.3,
        }}
        className="bg-white rounded-xl overflow-hidden max-w-md w-full shadow-2xl"
      >
        {/* Gradient header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-8 text-center relative overflow-hidden">
          {/* Animated sparkles in background */}
          <div className="absolute inset-0">
            <motion.div
              className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-white opacity-70"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.7, 0.9, 0.7],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
            <motion.div
              className="absolute top-3/4 left-1/3 w-1.5 h-1.5 rounded-full bg-white opacity-50"
              animate={{
                scale: [1, 1.8, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.5,
              }}
            />
            <motion.div
              className="absolute top-1/3 right-1/4 w-2 h-2 rounded-full bg-white opacity-60"
              animate={{
                scale: [1, 1.6, 1],
                opacity: [0.6, 0.9, 0.6],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 1,
              }}
            />
          </div>

          {/* Main icon with pulsing effect */}
          <div className="relative">
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
                opacity: [1, 0.8, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center mx-auto mb-5"
            >
              <Zap className="w-12 h-12 text-white" />
            </motion.div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">Upgrade Your Plan</h2>
          <p className="text-white text-opacity-90 max-w-xs mx-auto">
            {upgradeMessage || "Take your lead generation to the next level with our premium features"}
          </p>
        </div>

        {/* Content section */}
        <div className="p-6">
          {/* Feature highlights */}
          <div className="space-y-4 mb-6">
            {PLAN_HIGHLIGHTS.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.3 }}
                className="flex items-start"
              >
                <div className="mt-1 mr-3">{highlight.icon}</div>
                <div>
                  <h4 className="font-medium text-gray-800">{highlight.title}</h4>
                  <p className="text-sm text-gray-600">{highlight.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col space-y-3">
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 10px 15px -3px rgba(139, 92, 246, 0.15)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpgradeClick}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg flex items-center justify-center shadow-md"
            >
              <Award className="w-5 h-5 mr-2" />
              <span className="font-medium">Upgrade Now</span>
              <ChevronRight className="w-5 h-5 ml-1" />
            </motion.button>

            <motion.button
              whileHover={{ backgroundColor: "rgba(243, 244, 246, 1)" }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg transition-colors"
            >
              Maybe Later
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default React.memo(UpgradeModal);
